/**
 * Subscription status + danger-zone account actions.
 *
 * - "Leave this account": calls leave_account. Server-side at-least-
 *   one-admin invariant rejects the last admin (they must promote
 *   someone first or delete the account).
 * - "Delete this account": admin-only. Cascades through every
 *   account-scoped collection (members, calendars + events, devices,
 *   button_presses, alerts, alert_dispatches) and unbinds the device
 *   registry entries. Irreversible — no soft-delete in v1.
 */

import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { useAccount } from '@/hooks/useAccount';
import { useMyRole } from '@/hooks/useMyRole';
import { useBillingState, type BillingStateWithId } from '@/hooks/useBillingState';
import { Button } from '@/components/ui/Button';
import { Field } from '@/components/ui/Field';
import { Input } from '@/components/ui/Input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/Dialog';
import {
  createBillingPortalSession,
  createSubscriptionCheckout,
  deleteAccount,
  leaveAccount,
} from '@/lib/callables';
import type { Account } from '@/schemas';

interface Props {
  aid: string;
}

export function AccountSection({ aid }: Props) {
  const { account } = useAccount(aid);
  const { billing } = useBillingState(aid);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const role = useMyRole(aid);
  const isAdmin = role === 'admin';
  const [leaveConfirming, setLeaveConfirming] = useState(false);
  const [leaveBusy, setLeaveBusy] = useState(false);
  const [billingBusy, setBillingBusy] = useState(false);
  const [subBusy, setSubBusy] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // A subscription *record* exists once Stripe has ever created one. The
  // webhook leaves it in place after a cancellation, so this stays true for
  // a cancelled account — which is what the summary wants (it shows
  // "cancelled", not "no subscription").
  const hasSubscriptionRecord =
    billing?.billingState === 'subscribed' || Boolean(billing?.stripeSubscriptionId);
  // A *live* subscription excludes a cancelled one. Driving the CTA off this
  // (not the lingering record) lets a cancelled account re-subscribe.
  const hasLiveSubscription =
    hasSubscriptionRecord && account?.subscriptionStatus !== 'cancelled';
  const justSubscribed = searchParams.get('subscription') === 'success';
  // 'awaiting_connect' means hardware was bought and the subscription starts
  // at device claim — not a place to start one here. Also hide the CTA right
  // after a checkout: the webhook lags the redirect, so hasLiveSubscription
  // is briefly still false and a second click would double-subscribe.
  const canStartSubscription =
    isAdmin &&
    !hasLiveSubscription &&
    billing?.billingState !== 'awaiting_connect' &&
    !justSubscribed;

  async function onLeave() {
    setLeaveBusy(true);
    setError(null);
    try {
      await leaveAccount({ accountId: aid });
      navigate('/', { replace: true });
    } catch (e) {
      setError(humanCallableError(e));
      setLeaveBusy(false);
    }
  }

  async function openBillingPortal() {
    setBillingBusy(true);
    setError(null);
    try {
      const res = await createBillingPortalSession({ accountId: aid });
      // Hand off to Stripe's hosted customer portal.
      window.location.href = res.data.url;
    } catch (e) {
      setError(humanCallableError(e));
      setBillingBusy(false);
    }
  }

  async function startSubscription() {
    setSubBusy(true);
    setError(null);
    try {
      const res = await createSubscriptionCheckout({ accountId: aid });
      // Hand off to Stripe's hosted subscription checkout.
      window.location.href = res.data.url;
    } catch (e) {
      setError(humanCallableError(e));
      setSubBusy(false);
    }
  }

  return (
    <div className="space-y-4">
      <div className="rounded-md bg-paper-alt px-3 py-3 text-sm text-ink-soft">
        <BillingSummary
          status={account?.subscriptionStatus}
          billing={billing}
          hasSubscription={hasSubscriptionRecord}
        />
        {justSubscribed && !hasLiveSubscription && (
          <p className="mt-2 text-xs text-ink-muted">
            Subscription started — it may take a moment to appear here.
          </p>
        )}
        {canStartSubscription && (
          <div className="mt-3">
            <Button
              disabled={subBusy}
              onClick={() => void startSubscription()}
            >
              {subBusy ? 'Opening…' : 'Start subscription'}
            </Button>
            <p className="mt-2 text-xs text-ink-muted">
              £14.99/month. Already have a Voice PE button? This activates the
              service for it.
            </p>
          </div>
        )}
        {isAdmin && billing?.stripeCustomerId && (
          <div className="mt-3">
            <Button
              variant="secondary"
              disabled={billingBusy}
              onClick={() => void openBillingPortal()}
            >
              {billingBusy ? 'Opening…' : 'Manage billing'}
            </Button>
          </div>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {leaveConfirming ? (
          <>
            <span className="text-sm text-ink">
              Leave this account? You'll lose access immediately.
            </span>
            <Button variant="ghost" disabled={leaveBusy} onClick={() => setLeaveConfirming(false)}>
              Cancel
            </Button>
            <Button variant="danger" disabled={leaveBusy} onClick={() => void onLeave()}>
              {leaveBusy ? 'Leaving…' : 'Leave'}
            </Button>
          </>
        ) : (
          <>
            <Button variant="secondary" onClick={() => setLeaveConfirming(true)}>
              Leave this account
            </Button>
            {isAdmin && (
              <Button variant="danger" onClick={() => setDeleteOpen(true)}>
                Delete this account
              </Button>
            )}
          </>
        )}
      </div>
      {error && (
        <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>
      )}
      <p className="text-xs text-ink-muted">
        Delete-account is irreversible. It cancels the monthly subscription and
        removes every event, alert, button binding, and caregiver from this
        account. Your sign-in stays active but with no accounts attached.
      </p>

      <DeleteAccountDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        aid={aid}
        elderName={account?.elderName ?? ''}
      />
    </div>
  );
}

function DeleteAccountDialog({
  open,
  onOpenChange,
  aid,
  elderName,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  aid: string;
  elderName: string;
}) {
  const navigate = useNavigate();
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function reset() {
    setTyped('');
    setError(null);
    setBusy(false);
  }

  function onClose(next: boolean) {
    if (!next) reset();
    onOpenChange(next);
  }

  async function onDelete() {
    setBusy(true);
    setError(null);
    try {
      await deleteAccount({ accountId: aid });
      navigate('/', { replace: true });
    } catch (e) {
      setError(humanCallableError(e));
      setBusy(false);
    }
  }

  const canConfirm = typed === 'DELETE';

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogTitle>Delete this account</DialogTitle>
        <DialogDescription className="mt-1">
          This permanently removes the account
          {elderName ? ` for ${elderName}` : ''}: every calendar event,
          alert, button pairing, and caregiver, and <strong>cancels your
          monthly subscription</strong>. The physical button will need to be
          re-paired before it can speak again. This cannot be undone.
        </DialogDescription>
        <form
          className="mt-4 flex flex-col gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (canConfirm) void onDelete();
          }}
        >
          <Field label="Type DELETE to confirm" htmlFor="confirm-delete">
            <Input
              id="confirm-delete"
              value={typed}
              onChange={(ev) => setTyped(ev.target.value)}
              placeholder="DELETE"
              autoFocus
            />
          </Field>
          {error && (
            <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>
          )}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" onClick={() => onClose(false)}>
              Cancel
            </Button>
            <Button type="submit" variant="danger" disabled={busy || !canConfirm}>
              {busy ? 'Deleting…' : 'Delete account'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function formatUnix(secs: number | null | undefined): string {
  if (secs == null) return '';
  try {
    return new Date(secs * 1000).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return '';
  }
}

/** One-liner describing the billing state for the caregiver. Reads the
 * client-facing `subscriptionStatus` plus the richer billing/state doc
 * (trial end, next bill, pending cancel). */
function BillingSummary({
  status,
  billing,
  hasSubscription,
}: {
  status: Account['subscriptionStatus'] | undefined;
  billing: BillingStateWithId | null;
  hasSubscription: boolean;
}) {
  // Hardware bought, device not yet claimed — the trial starts on connect.
  if (billing?.billingState === 'awaiting_connect') {
    return (
      <>
        <strong>Free trial</strong>
        <span className="text-ink-muted">
          {' '}
          · starts when you set up your button
        </span>
      </>
    );
  }
  // No Stripe subscription yet (a BYO/comp account before it subscribes).
  // Without this the default-'trial' subscriptionStatus would mislabel an
  // unsubscribed account as "Free trial".
  if (!hasSubscription) {
    return <>No active subscription</>;
  }
  if (status === 'active') {
    const when = formatUnix(billing?.currentPeriodEnd);
    if (billing?.cancelAtPeriodEnd) {
      return (
        <>
          Subscription: <strong>active</strong>
          {when && <span className="text-ink-muted"> · cancels on {when}</span>}
        </>
      );
    }
    return (
      <>
        Subscription: <strong>active</strong>
        {when && <span className="text-ink-muted"> · next bill {when}</span>}
      </>
    );
  }
  if (status === 'cancelled') {
    return (
      <>
        Subscription: <strong>cancelled</strong>
      </>
    );
  }
  // A real, trialing subscription (starts at device claim for every device).
  // awaiting_connect / no-subscription are handled above. Branch on card-on-file
  // like the trial emails do: a card-ful trial auto-bills, a card-less
  // (handed-out / BYO) trial has no bill and needs a card added to continue.
  const trialEnds = formatUnix(billing?.trialEnd);
  const hasCard = Boolean(billing?.stripePaymentMethodId);
  return (
    <>
      <strong>Free trial</strong>
      {trialEnds &&
        (hasCard ? (
          <span className="text-ink-muted"> · first bill {trialEnds}</span>
        ) : (
          <span className="text-ink-muted"> · ends {trialEnds} — add a payment method to continue</span>
        ))}
    </>
  );
}

function humanCallableError(e: unknown): string {
  if (e instanceof Error) {
    return e.message.replace(/^FirebaseError:\s*/, '');
  }
  return 'Action failed.';
}
